/* Original geometric winter decorations. No libraries, images, cookies or requests. */
(() => {
  if (customElements.get('hjp-winter-effects')) return;
  const TAU = Math.PI * 2;
  const PREFERENCE_KEY = 'hjp-winter-animation-paused-v1';

  class WinterEffects extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      this.button = this.querySelector('button');
      this.context = this.canvas.getContext('2d');
      if (!this.context) return;
      this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.media.matches;
      try {
        const preference = sessionStorage.getItem(PREFERENCE_KEY);
        if (preference !== null && !this.media.matches) this.paused = preference === 'true';
      } catch (_) { /* Animation remains usable when storage is unavailable. */ }
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.button.addEventListener('click', () => {
        this.paused = !this.paused;
        try { sessionStorage.setItem(PREFERENCE_KEY, String(this.paused)); } catch (_) { /* optional */ }
        this.sync();
      }, options);
      this.onMotionChange = () => { this.paused = this.media.matches; this.sync(); };
      this.media.addEventListener('change', this.onMotionChange);
      window.addEventListener('resize', () => this.resize(), options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.tick = this.tick.bind(this);
      this.resize();
      this.button.hidden = false;
      this.sync();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.events?.abort();
      this.media?.removeEventListener('change', this.onMotionChange);
    }

    resize() {
      this.width = document.documentElement.clientWidth;
      this.height = window.innerHeight;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      this.canvas.width = Math.round(this.width * ratio);
      this.canvas.height = Math.round(this.height * ratio);
      this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
      const density = Math.max(20, Math.min(80, Number(this.dataset.density) || 50));
      const count = Math.round(density * (this.width < 750 ? 0.5 : 1));
      this.particles = Array.from({ length: count }, (_, i) => this.particle(i, true));
    }

    particle(i, initial = false) {
      const mode = this.dataset.mode || 'mixed';
      // A mixed set contains 78% snow, 16% stars and 6% small trees.
      const choice = ((i * 19 + 47) % 50) / 50;
      const type = mode === 'mixed' ? (choice < 0.78 ? 'snow' : choice < 0.94 ? 'stars' : 'trees') : mode;
      const speed = { slow: 0.7, gentle: 1, brisk: 1.4 }[this.dataset.speed] || 1;
      return {
        type, x: Math.random() * this.width,
        y: initial ? Math.random() * (this.height + 60) - 30 : -30,
        radius: type === 'trees' ? 9 + Math.random() * 4 : type === 'stars' ? 5 + Math.random() * 4 : 2.5 + Math.random() * 4,
        fall: (15 + Math.random() * 19) * speed,
        sway: 7 + Math.random() * 11,
        phase: Math.random() * TAU,
        rotation: (Math.random() - 0.5) * 0.5,
        alpha: type === 'snow' ? 0.65 + Math.random() * 0.3 : 0.7 + Math.random() * 0.2
      };
    }

    sync() {
      cancelAnimationFrame(this.frame);
      this.last = 0;
      this.button.setAttribute('aria-pressed', String(!this.paused));
      this.button.setAttribute('aria-label', this.paused ? 'Resume winter animation' : 'Pause winter animation');
      this.button.querySelector('span').textContent = this.paused ? 'Resume animation' : 'Pause animation';
      this.canvas.hidden = this.paused;
      if (!this.paused && !document.hidden) this.frame = requestAnimationFrame(this.tick);
    }

    tick(now) {
      if (!this.isConnected || this.paused || document.hidden) return;
      this.frame = requestAnimationFrame(this.tick);
      if (!this.last) { this.last = now; return; }
      const elapsed = now - this.last;
      if (elapsed < 1000 / 30) return;
      const dt = Math.min(elapsed / 1000, 0.08);
      this.last = now;
      this.context.clearRect(0, 0, this.width, this.height);
      for (const p of this.particles) {
        p.y += p.fall * dt;
        p.phase += dt * 0.55;
        if (p.y > this.height + 30) { p.y = -30; p.x = Math.random() * this.width; }
        this.draw(p);
      }
    }

    draw(p) {
      const c = this.context;
      c.save();
      c.globalAlpha = p.alpha;
      c.translate(p.x + Math.sin(p.phase) * p.sway, p.y);
      c.rotate(p.rotation + Math.sin(p.phase * 0.65) * (p.type === 'trees' ? 0.15 : 0.45));
      const r = p.radius;
      if (p.type === 'trees') {
        c.fillStyle = '#ac8563';
        c.fillRect(-r * 0.12, r * 0.55, r * 0.24, r * 0.4);
        c.fillStyle = '#648b79';
        c.strokeStyle = '#fff9f5';
        c.lineWidth = 0.65;
        c.beginPath();
        c.moveTo(0, -r);
        c.lineTo(r * 0.54, -r * 0.13);
        c.lineTo(r * 0.3, -r * 0.13);
        c.lineTo(r * 0.76, r * 0.65);
        c.lineTo(-r * 0.76, r * 0.65);
        c.lineTo(-r * 0.3, -r * 0.13);
        c.lineTo(-r * 0.54, -r * 0.13);
        c.closePath(); c.fill(); c.stroke();
        c.fillStyle = '#e2c16f';
        c.beginPath(); c.arc(0, -r, 1.5, 0, TAU); c.fill();
      } else if (p.type === 'stars') {
        c.fillStyle = '#d6b367';
        c.strokeStyle = '#a4804b'; c.lineWidth = 0.45;
        c.beginPath();
        for (let i = 0; i < 10; i++) {
          const angle = i * Math.PI / 5 - Math.PI / 2;
          const length = i % 2 ? r * 0.44 : r;
          const x = Math.cos(angle) * length, y = Math.sin(angle) * length;
          if (i === 0) c.moveTo(x, y); else c.lineTo(x, y);
        }
        c.closePath(); c.fill(); c.stroke();
      } else {
        // A faint blue-grey edge keeps white snow readable on the cream theme.
        c.strokeStyle = '#90a5ad'; c.lineWidth = 2.2; this.snow(c, r);
        c.strokeStyle = '#ffffff'; c.lineWidth = 1.25; this.snow(c, r);
      }
      c.restore();
    }

    snow(c, r) {
      c.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = i * Math.PI / 3;
        const x = Math.cos(angle) * r, y = Math.sin(angle) * r;
        c.moveTo(0, 0); c.lineTo(x, y);
        if (r > 4) {
          for (const side of [-1, 1]) {
            c.moveTo(x * 0.57, y * 0.57);
            c.lineTo(x * 0.57 + Math.cos(angle + side * Math.PI / 3) * r * 0.3,
              y * 0.57 + Math.sin(angle + side * Math.PI / 3) * r * 0.3);
          }
        }
      }
      c.stroke();
    }
  }
  customElements.define('hjp-winter-effects', WinterEffects);
})();
